/**
 * Quiet motion for the Unit theme. Everything here is progressive: without this script (or with
 * "reduce motion" switched on) the page renders exactly as before, fully visible.
 *
 * - Reveal: cards, heading rows, tiles and feature splits rise and fade in once as they enter view,
 *   staggered a little across a row. Styles live under html.unit-motion in assets/unit.css.
 * - Image fade: card, tile and product images fade up when loaded instead of popping in.
 * - Header tuck: scrolling down tucks the header away; scrolling up brings it back.
 * - Card to product: the clicked card's image is named for the cross-page view transition, so it
 *   glides into the product page's main image (the receiving side is in snippets/unit-brand-head.liquid).
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;

  const REVEAL_SELECTOR = '.unit-card, .unit-heading-row, .unit-tile, .unit-split, .unit-collection__header, .unit-story__inner, .unit-artist';
  const IMAGE_SELECTOR = '.unit-card__media img:first-of-type, .unit-tile img, .unit-split__media img';
  const STAGGER_MS = 90;
  const MAX_STAGGER_STEPS = 4;

  /* ---------- Image fade ---------- */

  function watchImage(img) {
    if (img.dataset.unitImage) return;
    img.dataset.unitImage = '';
    const done = () => img.classList.add('is-loaded');
    if (img.complete && img.naturalWidth > 0) done();
    else {
      img.addEventListener('load', done, { once: true });
      // A broken image should still show its frame rather than stay invisible.
      img.addEventListener('error', done, { once: true });
    }
  }

  /* ---------- Reveal ---------- */

  const revealObserver =
    !reduceMotion && 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  function watchReveal(el) {
    if (!revealObserver || el.dataset.unitReveal !== undefined) return;
    el.dataset.unitReveal = '';
    // Stagger by position in its row, so a row arrives left to right rather than all at once.
    const item = el.closest('.unit-grid > *, .unit-tiles > *');
    if (item) {
      const grid = item.parentElement;
      const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length || 1;
      const step = Math.min([...grid.children].indexOf(item) % columns, MAX_STAGGER_STEPS);
      el.style.setProperty('--unit-reveal-delay', `${step * STAGGER_MS}ms`);
    }
    revealObserver.observe(el);
  }

  function scan(scope = document) {
    scope.querySelectorAll(IMAGE_SELECTOR).forEach(watchImage);
    scope.querySelectorAll(REVEAL_SELECTOR).forEach(watchReveal);
  }

  if (!reduceMotion) root.classList.add('unit-motion');
  scan();
  // Sections re-rendered by Horizon (filters, sorting, the theme editor) bring in new elements.
  new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => node.nodeType === 1 && scan(node));
    }
  }).observe(document.body, { childList: true, subtree: true });

  /* ---------- Header tuck ---------- */

  const TUCK_AFTER_PX = 240;
  const DELTA_PX = 6;
  let lastY = 0;

  function scrollTop() {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY, wrapper?.scrollTop || 0);
  }

  function onScroll() {
    const y = scrollTop();
    if (Math.abs(y - lastY) < DELTA_PX) return;
    const tuck = y > lastY && y > TUCK_AFTER_PX;
    document.body.classList.toggle('unit-header-tucked', tuck);
    lastY = y;
  }

  // The page can scroll inside .page-wrapper rather than the window; the capture phase hears both.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });

  /* ---------- Card to product ---------- */

  const TRANSITION_NAME = 'unit-artwork';

  function clearNames() {
    document.querySelectorAll('[data-unit-vt]').forEach((el) => {
      el.style.viewTransitionName = '';
      delete el.dataset.unitVt;
    });
  }

  document.addEventListener('click', (event) => {
    if (reduceMotion) return;
    const card = event.target.closest('a.unit-card');
    if (!card || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    const img = card.querySelector('.unit-card__media img:first-of-type');
    if (!img) return;
    clearNames();
    img.style.viewTransitionName = TRANSITION_NAME;
    img.dataset.unitVt = '';
  });

  // Coming back via the back button restores the old page as it was; drop the name so the next
  // click doesn't find two images sharing it.
  window.addEventListener('pageshow', clearNames);
})();
